






































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Collection, Document, ChatMessage, Participant, Room, QueuedSong } from "@/types";

import ChitChat from "@/components/room/ChitChat.vue";
import VideoGrid from "@/components/room/VideoGrid.vue";
import SideBar from "@/components/room/SideBar.vue";
import AppBar from "@/components/room/AppBar.vue";
import RoomSettings from "@/components/room/RoomSettings.vue";
import ShareLink from "@/components/room/ShareLink.vue";

import ModalView from "@/components/design/ModalView.vue";

import * as firebase from "firebase/app"; // for types
import * as twilio from "twilio-video";

@Component({
  components: {
    ChitChat,
    AppBar,
    SideBar,
    VideoGrid,
    RoomSettings,
    ModalView,
    ShareLink,
  },
})
export default class KaraokeRoom extends Vue {
  @Prop({ required: true })
  initialStream!: MediaStream;

  myStream!: MediaStream | null;

  @Prop({ required: true })
  remoteParticipants!: twilio.RemoteParticipant[];

  @Prop({ required: true })
  localParticipant!: twilio.LocalParticipant;

  @Prop({ required: true })
  room!: Document<Room>;

  @Prop({ required: true })
  me!: Document<Participant>;

  @Prop({ required: true })
  participants!: Collection<Participant>;

  @Prop({ required: true })
  songqueue!: Collection<QueuedSong>;

  @Prop({ required: true })
  chat!: Collection<ChatMessage>;

  // @ts-ignore: webkitAudioContext is not known
  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  songAudioOutput = this.audioCtx.createGain();
  combinerNode = this.audioCtx.createMediaStreamDestination();
  micInput: AudioNode | null = null;

  showChat = false;
  showRoomSettings = false;
  showInvite = false;

  streamState = {
    micMuted: false,
    videoMuted: false,
  };

  participantMute = false;

  unsubscribes: { (): void }[] = [];

  created() {
    this.myStream = this.initialStream;
    this.audioCtx.resume();

    this.micInput = this.audioCtx.createMediaStreamSource(this.myStream);
    this.micInput.connect(this.combinerNode);

    const delayNode = this.audioCtx.createDelay(2);
    delayNode.delayTime.value = 0.15;
    this.songAudioOutput.connect(delayNode);
    delayNode.connect(this.combinerNode);

    //this.songAudioOutput.connect(this.audioCtx.destination);

    this.localParticipant.publishTrack(this.myStream.getVideoTracks()[0]);
    this.localParticipant.publishTrack(this.combinerNode.stream.getAudioTracks()[0]);

    this.unsubscribes.push(
      this.chat.ref.where("added", ">", firebase.firestore.Timestamp.now()).onSnapshot(querySnapshot => {
        querySnapshot.docChanges().forEach(change => {
          if (change.type === "added") {
            (this.$refs.appBar as AppBar).newChat();
          }
        });
      }),
    );
  }

  @Watch("room.data", { immediate: true })
  roomChange(room: Room) {
    if (!room.currentSong) {
      this.participantMute = false;
    } else if (room.currentVideoPaused) {
      this.participantMute = false;
    } else if (
      room.currentVideoTime <= 10 ||
      (room.currentVideoDuration > 10 && room.currentVideoDuration - 10 <= room.currentVideoTime)
    ) {
      this.participantMute = false;
    } else if (room.currentSong.singer !== this.me.ref.id) {
      this.participantMute = true;
    } else {
      this.participantMute = false;
    }
  }

  get shouldMuteMic() {
    return this.streamState.micMuted || this.participantMute;
  }

  @Watch("shouldMuteMic", { immediate: true })
  handleMuteUnmute(micMuted: boolean) {
    this.localParticipant.audioTracks.forEach(publication => {
      if (publication.track) {
        if (micMuted) {
          publication.track.disable();
        } else {
          publication.track.enable();
        }
      }
    });
  }

  async updateSettings(name: string, newStream: MediaStream) {
    if (name !== this.me.data.name) {
      this.me.ref.update({ name: name });
    }
    if (this.myStream !== newStream) {
      this.micInput.disconnect(this.combinerNode);
      this.myStream.getAudioTracks()[0].stop();
      this.micInput = this.audioCtx.createMediaStreamSource(newStream);
      this.micInput.connect(this.combinerNode);

      const newVideoTrack = newStream.getVideoTracks()[0];
      this.localParticipant.videoTracks.forEach(publication => {
        if (
          publication.track &&
          publication.track.mediaStreamTrack.getSettings().deviceId !== newVideoTrack.getSettings().deviceId
        ) {
          publication.track.stop();
        }
        this.localParticipant.unpublishTrack(publication.track);
        this.localParticipant.publishTrack(newVideoTrack);
      });

      this.myStream = newStream;
    }
    this.showRoomSettings = false;
  }

  toggleAudio() {
    const newState = !this.streamState.micMuted;
    this.$set(this.streamState, "micMuted", newState);
  }

  toggleVideo() {
    const newState = !this.streamState.videoMuted;
    this.$set(this.streamState, "videoMuted", newState);
    this.localParticipant.videoTracks.forEach(publication => {
      if (publication.track) {
        if (newState) {
          publication.track.disable();
        } else {
          publication.track.enable();
        }
      }
    });
  }

  toggleChat() {
    this.showChat = !this.showChat;
    if (this.showChat) {
      (this.$refs.chat as ChitChat).focus();
    }
  }

  get myId() {
    return this.me.ref && this.me.ref.id;
  }

  role(participantId: string) {
    if (this.isMe(participantId)) {
      return "me";
    }
    if (this.myId < participantId) {
      return "rude";
    }
    return "polite";
  }

  isMe(participantId: string) {
    return this.myId === participantId;
  }

  beforeDestroy() {
    this.unsubscribes.forEach(f => f());
  }
}
